import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import "./header.css";
import userimg from "../image/user.svg";
import menuicon from "../image/menu.svg";
import { useSidebar } from "../context/SidebarContext";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import api from "../api/api";
import Badge from "@mui/material/Badge";
import io from "socket.io-client";
 
 
 
const Header = () => {
  const { user, logout } = useAuth();
  // console.log("header user", user);
 
  const { isOpen } = useSidebar();
  const [dropdown, setDropdown] = useState(false);
  const [unifiedNotificationDropdown, setUnifiedNotificationDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [unverifiedUsers, setUnverifiedUsers] = useState(0);
  const [gstUpdateRequests, setGstUpdateRequests] = useState(0);
  const [warehouseRequests, setWarehouseRequests] = useState(0);
  const [totalNotificationCount, setTotalNotificationCount] = useState(0);
  const [socket, setSocket] = useState(null);
  const dropdownRef = useRef(null);
  const unifiedNotificationRef = useRef(null);
  const timeoutRef = useRef(null);
  const fetchNotificationsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleSidebar } = useSidebar();
 
  const toggleDropdown = () => setDropdown((prev) => !prev);
 
  // Function to fetch notifications - defined outside useEffect for accessibility
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token || !user) return;
 
      // Fetch notifications
      const notificationsResponse = await api.get(`/notifications/user/${user.user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userNotifications = Array.isArray(notificationsResponse.data.data.notifications) ? notificationsResponse.data.data.notifications.filter(
        (notif) => !notif.is_read
      ) : [];
      setNotifications(userNotifications);
      setNotificationCount(userNotifications.length);
 
      // Fetch unverified users and GST requests for admins
      if (user?.user_role === "Admin" || user?.user_role === "SuperAdmin") {
        const { data } = await api.get("/dashboard/kpis", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const unverified = data.Unverified_users || 0;
        const gstRequests = data.gst_update_request || 0;
        const whRequests = data.warehouse_requests || 0;
        setUnverifiedUsers(unverified);
        setGstUpdateRequests(gstRequests);
        setWarehouseRequests(whRequests);
 
        // Calculate total notification count (activity + verification)
        const verificationCount = unverified + gstRequests + whRequests;
        setTotalNotificationCount(userNotifications.length + verificationCount);
      } else {
        // For non-admin users, total count is just activity notifications
        setTotalNotificationCount(userNotifications.length);
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  }, [user]);
 
  // Update the ref whenever fetchNotifications changes
  useEffect(() => {
    fetchNotificationsRef.current = fetchNotifications;
  }, [fetchNotifications]);
 
  // Socket.IO connection and real-time updates
  useEffect(() => {
    if (user) {
      // Connect to Socket.IO
      const socketConnection = io(process.env.REACT_APP_SOCKET_URL || 'http://localhost:4000', {
        query: {
          userId: user.user_id
        }
      });
 
      // Join user room and role-based rooms
      socketConnection.emit('join', {
        user_id: user.user_id,
        user_role: user.user_role
      });
 
      // Listen for notification read events (when other admins mark as read)
      socketConnection.on('notificationRead', (data) => {
        console.log('Notification marked as read by another admin:', data);
        // Refresh notifications and count using ref
        if (fetchNotificationsRef.current) {
          fetchNotificationsRef.current();
        }
      });
 
      // Listen for bulk read events
      socketConnection.on('bulkRead', (data) => {
        console.log('Bulk read performed by another admin:', data);
        // Refresh notifications and count using ref
        if (fetchNotificationsRef.current) {
          fetchNotificationsRef.current();
        }
      });
 
      // Listen for new notifications
      socketConnection.on('newNotification', (data) => {
        console.log('New notification received:', data);
        // Refresh notifications and count using ref
        if (fetchNotificationsRef.current) {
          fetchNotificationsRef.current();
        }
      });
 
      setSocket(socketConnection);
 
      // Cleanup on unmount
      return () => {
        socketConnection.disconnect();
      };
    }
  }, [user, fetchNotifications]);
 
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdown(false);
      }
      if (
        unifiedNotificationRef.current &&
        !unifiedNotificationRef.current.contains(event.target)
      ) {
        setUnifiedNotificationDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
 
  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);
 
  const handleLogout = () => {
    logout();
    navigate("/login");
  };
 
  const markNotificationAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      // Update local state to reflect the change
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      setNotificationCount(prev => Math.max(0, prev - 1));
      // Update total count for admins
      setTotalNotificationCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
 
  const markNotificationsByEnquiry = async (enqId, type) => {
    try {
      await api.put(`/notifications/mark-by-enquiry/${enqId}`, { type });
      // Update count by fetching current unread count
      const countResponse = await api.get(`/notifications/unread/${user.user_id}`);
      setNotificationCount(countResponse.data.data.unreadCount || 0);
    } catch (error) {
      console.error('Error marking notifications by enquiry:', error);
    }
  };
 
  const generateTitleFromPath = (path) => {
    // Split path and clean empty parts
    const parts = path.split("/").filter(Boolean);
 
    if (parts.length === 0) return "Dashboard";
 
    // Handle special case: dynamic detail pages
    if (path.includes("/view-enquiry-details/")) return "View Enquiry";
    if (path.includes("/edit-enquiry/")) return "Edit Enquiry";
    if (path.includes("/view-quotation/")) return "View Quotation";
    if (path.includes("/send-quotation/")) return "Send Quotation";
    if (path.includes("/view-booking/")) return "List of Booking";
    if (path.includes("/view-bookings/")) return "View Booking";
    if (path.includes("/edit-bookings/admin")) return "Edit Bookings";
 
    if (path.includes("/my-bookings/list-of-bookings/"))
      return "List Of Bookings";
    if (path.includes("/create-shipping-instructions/"))
      return "Create Shipping Instructions";
    if (path.includes("/list-of-bl/admin"))
      return "List Of Booking for Shipping Instruction";
    if (path.includes("/view-bl-draft")) return "View BL Draft";
    if (path.includes("/edit-shipping-instructions"))
      return "Edit Shipping Instructions";
    if (path.includes("/create-si/admin"))
      return "Create Shipping Instructions";
    if (path.includes("/view-si/admin/")) return "View Shipping Instructions";
    if (path.includes("/terms-and-conditions")) return "Terms & Conditions";
    if (path.includes("/master-data/ports")) return "Port List";
    if (path.includes("/master-data/charge-code")) return "Charge Code";
   if(path.includes("/master-data/bank-master")) return "Bank Master";
    if (path.includes("/master-data/vendor-master")) return "Vendor Master";
   
    if(path.includes("/edit-vendor/") )return "Edit Vendor";
       if (path.includes("/vendor-onboarding-form")) return "Vendor Onboarding Form";
       if(path.includes("/view-vendor-onboarding")) return "View Vendor Onboarding";
       if(path.includes("/view-client-onboarding")) return "View Client Onboarding";
           if (path.includes("/client-onboarding-form")) return "Client Onboarding Form";
    if(path.includes("/create-performa-invoice")) return "Create Performa Invoice";
    if(path.includes("/view-performa-invoice")) return "View Performa Invoice";
    if (path.includes("/edit-container-status"))
      return "View All Operations";
     if (path.includes("/operations"))
      return "Operations";
    if (path.includes("/commercial"))
      return "Commercial";
    if(path.includes("/documents")) return "Documents";
    if (path.includes("/view-booking-documents"))
      return "View Booking Documents";
    if (path.includes("/upload-documents")) return "Documents";
    if (path.includes("/create-enquiry-sales")) return "Create Enquiry ";
    if (path.includes("/sales-view-enquiry")) return "View Enquiry ";
    if(path.includes("/create-warehouses-request")) return "Create Warehouses Request"
    if (path.includes("/warehouse")) return "List Of Warehouse Requests";
    if (path.includes("/warehouses-request")) return "Warehouses Requests";
    if(path.includes("/view-wh-request"))  return "View Warehouse Request"
    if (path.includes("/confirm-warehouse"))
      return "Warehouses Request Response";
    if (path.includes("/list-wh-admin")) return "List of WareHouse";
    if(path.includes("/warehouse-bookings")) return "Warehouse Bookings";
    if(path.includes("/user-documents")) return "User Documents";
    if (path.includes("/warehouse-request-responses")) return "Warehouse Request Responses";
    if(path.includes("/my-warehouses")) return "My Warehouses";
    if(path.includes("/create-warehouse")) return "Create Warehouse";
    if(path.includes("/warehouse-add-requests")) return "Warehouse Add Requests";
     if(path.includes("/view-warehouse-details")) return "Warehouse Details";
      if(path.includes("/edit-warehouse-requests")) return "Edit Warehouse Requests";
    if(path.includes("/all-notifications")) return "All Notifications"
 
    if(path.includes("/unverified-users")) return "Unverified Users";
    if(path.includes("/gst-update-requests")) return "GST Update Requests"
    if(path.includes("/total-followups")) return "Total Followups";
    if(path.includes("/view-followup-details")) return "View Followup Details"
    if(path.includes("/special-enquiries")) return "Special Enquiries";
    if(path.includes("/high-volume-customers")) return "High Volume Customers";
    if(path.includes("/sales-bookings")) return "Confirmed Bookings";
    if(path.includes("/cha-requests")) return "Cha Requests";
    if(path.includes("/cha-services-requests")) return "Cha Services Requests";
    if(path.includes("/audit-details")) return "Audit Details";
    if(path.includes("/purchase-details")) return "Purchase Details";
    if(path.includes("/invoice-details")) return "Tax Invoice";
  
    if(path.includes("/billingAndInvoice")) return "Invoicing";
    if(path.includes("/intelligence")) return "Intelligence";
    if(path.includes("/my-profile")) return "My Profile";
 
 
    // Otherwise use last segment as title
    const lastPart = parts[parts.length - 1];
    let title = lastPart
      .replace(/[-_]/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return title;
  };
 
  const currentPageTitle = generateTitleFromPath(location.pathname);
 
 
 
  return (
    <div className="mainhdr">
      <div className={isOpen ? "container-fluid" : "container-fluid"}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <button className="sidebar-menu-toggle" onClick={toggleSidebar}>
              <img src={menuicon} alt="menuicon" />
            </button>
            <div className="ms-3 d-none d-md-block">
              <h2>{currentPageTitle}</h2>
 
              {currentPageTitle === "View BL Draft" ? (
                <span className="logo-text">
                  View BL Draft for your booking
                </span>
              ) : currentPageTitle === "Create Enquiry" ? (
                <span className="logo-text">Create a New Enquiry</span>
              ) : currentPageTitle === "Dashboard" ? (
                <span className="logo-text">
                  View and Manage {currentPageTitle}
                </span>
              ) : currentPageTitle === "Details of your Booking" ? (
                <span className="logo-text">Details of all your bookings</span>
              ) : currentPageTitle === "Create Shipping Instructions" ? (
                <span className="logo-text">
                  Create Shipping Instructions for your bookings
                </span>
              ) : currentPageTitle === "View Enquiry" ? (
                <span className="logo-text">Details of all your enquiries</span>
              ) : currentPageTitle === "Edit Enquiry" ? (
                <span className="logo-text">Edit your current enquiry</span>
              ) : currentPageTitle === "Pending Enquiries" ? (
                <span className="logo-text">
                  Details of all enquiries pending for a Quotation
                </span>
              ) : currentPageTitle === "Edit Bookings" ? (
                <span className="logo-text">
                  Edit your Customers current Bookings
                </span>
              ) : currentPageTitle === "Quotation Details" ? (
                <span className="logo-text">
                  Please Check all the details and Send the Quotation
                </span>
 
              ) : currentPageTitle === "Documents" ? (
                <span className="logo-text">
                  Upload All Required Booking Documents
                </span>
              ) : currentPageTitle === "Send Quotation" ? (
                <span className="logo-text">
                  Please Check all the details and Send the Quotation
                </span>
              ) : currentPageTitle === "View Quotation" ? (
                <span className="logo-text">
                  Please Check all the details of your Quotation
                </span>
              ) : currentPageTitle === "Quotation" ? (
                <span className="logo-text">
                  Please Check all the details and accept the Quotation
                </span>
              ) : currentPageTitle === "List of Booking" ? (
                <span className="logo-text">Details of all your Booking</span>
              ) : currentPageTitle === "List Of Bookings" ? (
                <span className="logo-text">Details of all your Bookings</span>
              ) : currentPageTitle === "View Booking" ? (
                <span className="logo-text">Details of your booking</span>
              ) : currentPageTitle === "User Profile" ? (
                <span className="logo-text">View and Manage Your Profile</span>
              ) : currentPageTitle === "My Profile" ? (
                <span className="logo-text">View and Manage Profile</span>  
              ) : currentPageTitle === "View Bl Draft" ? (
                <span className="logo-text">View and Manage Your BL Draft</span>
              ) : currentPageTitle === "Edit Shipping Instructions" ? (
                <span className="logo-text">
                  Edit Shipping Instructions Details Of your Bookings
                </span>
              ) : currentPageTitle === "List Of Bl" ? (
                <span className="logo-text">Details of all your BL's</span>
              ) : currentPageTitle ===
                "List Of Booking for Shipping Instruction" ? (
                <span className="logo-text">
                  Details of all bookings pending for Shipping Instructions
                </span>
              ) : currentPageTitle === "View Shipping Instructions" ? (
                <span className="logo-text">
                  View Shipping Instructions for the Booking
                </span>
              ) : currentPageTitle === "Exchange Rate" ? (
                <span className="logo-text">
                  View And Manage Exchange Rates
                </span>
              ) : currentPageTitle === "Port List" ? (
                <span className="logo-text">
                   View And Manage All Ports
                </span>  
              ) : currentPageTitle === "Charge Code" ? (
                <span className="logo-text">
                   View And Manage Charge Codes
                </span>  
              ) : currentPageTitle === "Job Master" ? (
                <span className="logo-text">
                  View And Manage Job Master
                </span>
 
     
              ) : currentPageTitle === "Bank Master" ? (
                <span className="logo-text">
                  View And Manage Bank Master
                </span>
 
     
 
 
 
 
                ) : currentPageTitle === "Vendor Master" ? (
                <span className="logo-text">View And Manage Vendor List</span>
                 ) : currentPageTitle === "Edit Vendor" ? (
                <span className="logo-text">View And Manage Edit Vendor</span>
                 ) : currentPageTitle === "View Vendor Onboarding " ? (
                <span className="logo-text">View And Manage Onboarding</span>
 
                 ) : currentPageTitle === "View Client Onboarding " ? (
                <span className="logo-text">View And Manage Client Onboarding</span>
                 ) : currentPageTitle === "Vendor Onboarding Form" ? (
                <span className="logo-text">Manage Onboarding </span>
 
                  ) : currentPageTitle === "Client Onboarding Form" ? (
                <span className="logo-text">Manage Client Boarding</span>
               
                  ) : currentPageTitle === "View Vendor Onboarding" ? (
                <span className="logo-text">View And Manage Vendor Onboarding</span>
                 ) : currentPageTitle === "Documents" ? (
                <span className="logo-text">
                  View And Manage Documents
                </span>
              ) : currentPageTitle === "View All the Operation" ? (
                <span className="logo-text">View And Manage Operations</span>
               ) : currentPageTitle === "Commercial" ? (
                <span className="logo-text">View And Manage Commercial</span>  
              ) : currentPageTitle === "View Booking Documents" ? (
                <span className="logo-text">
                  View and manage all shipping documents
                </span>
                ) : currentPageTitle === "Vendor Master" ? (
                <span className="logo-text">View And Manage Vendor List</span>
                 ) : currentPageTitle === "Vendor Onboarding Form" ? (
                <span className="logo-text">Manage Onboarding </span>
              ) : currentPageTitle === "Client Master" ? (
                <span className="logo-text">View And Manage all Clients </span>
                  ) : currentPageTitle === "Client Onboarding Form" ? (
                <span className="logo-text">Manage Client Boarding</span>
              ) : currentPageTitle === "User Master" ? (
                <span className="logo-text">View And Manage all Users </span>
              ) : currentPageTitle === "Terms & Conditions" ? (
                <span className="logo-text">
                  View And Manage All Terms And Conditions
                </span>
              ) : currentPageTitle === "Container Status Report" ? (
                <span className="logo-text">Edit Container Status Report</span>
                 ) : currentPageTitle === "Operations" ? (
                <span className="logo-text">View And Manage Operations</span>
              ) : currentPageTitle === "Freight Enquiries" ? (
                <span className="logo-text">
                  View And Manage All Your Enquiries
                </span>
              ) : currentPageTitle === "Create Enquiry" ? (
                <span className="logo-text">Create a New Enquiry</span>
              ) : currentPageTitle === "Sales Dashboard" ? (
                <span className="logo-text">
                  View And Manage Sales Activites
                </span>
              ) : currentPageTitle === "Sales Clients" ? (
                <span className="logo-text">
                  View And Manage All Clients Details
                </span>
              ) : currentPageTitle === "Sales Enquiries" ? (
                <span className="logo-text">
                  View And Manage All Sales Enquiries
                </span>
              ) : currentPageTitle === "Total Followups" ? (
                <span className="logo-text">
                  View And Manage All Followups
                </span>
               ) : currentPageTitle === "View Followup Details" ? (
                <span className="logo-text">
                  View And Manage View Followup details
                </span>  
              ) : currentPageTitle === "Confirmed Bookings" ? (
                <span className="logo-text">
                  View And Manage All Confirmed Bookings
                </span>
              ) : currentPageTitle === "Special Enquiries" ? (
                <span className="logo-text">
                  View And Manage All Special Enquiries
                </span>
               ) : currentPageTitle === "High Volume Customers" ? (
                <span className="logo-text">
                  View And Manage All High Volume Customers
                </span>
              ) : currentPageTitle === "View Enquiry " ? (
                <span className="logo-text">Details Of Your Enquiry</span>
              ) : currentPageTitle === "List Of Warehouse Requests" ? (
                <span className="logo-text">
                  Track And Manage Your Warehouse Storage Requests
                </span>
              ) : currentPageTitle === "Create Warehouses Request" ? (
                <span className="logo-text">
                  Create And Manage Warehouses Preference
                </span>
             ) : currentPageTitle === "Edit Warehouse Requests" ? (
                <span className="logo-text">
                 Edit Warehouse Requests Manage
                </span>  
            ) : currentPageTitle === "Warehouse Request" ? (
                <span className="logo-text">
                  Create a new Warehousing Requests
                </span>
              ) : currentPageTitle === "Warehouses Request Response" ? (
                <span className="logo-text">
                  View & Book the Available Warehouses
                </span>
              ) : currentPageTitle === "Unverified Users" ? (
                <span className="logo-text">View And Manage All Unverifed Users</span>
                  ) : currentPageTitle === "GST Update Requests" ? (
                <span className="logo-text">View And Manage All GST Update Requests</span>
              ) : currentPageTitle === "View Warehouse Request" ? (
                <span className="logo-text">Detail Of Warehouse Request</span>
              ): currentPageTitle === "List of WareHouse" ? (
                <span className="logo-text">View All The Warehouse Requests</span>
              ): currentPageTitle === "Warehouse Request Responses" ? (
                <span className="logo-text">
                 View The  Response of your Warehouse Storage Requests
                </span>  
              ): currentPageTitle === "Warehouse Bookings" ? (
                <span className="logo-text">View And Manage All Warehouse Bookings Confirmed</span>
              ): currentPageTitle === "My Warehouses" ? (
                <span className="logo-text">
                 List of All Warehouses
                </span>  
              ): currentPageTitle === "Create Warehouse" ? (
                <span className="logo-text">
                Enter The Following Details To Add A New Warehouse.
                </span>
                ): currentPageTitle === "Warehouse Add Requests" ? (
                <span className="logo-text">
               View All Warehouse Add Requests
                </span>
                 ): currentPageTitle === "Warehouse Details" ? (
                <span className="logo-text">
               Verify Or Reject The Warehouse Request Details
                </span>
                  ): currentPageTitle === "All  Notifications" ? (
                <span className="logo-text">
               View All The Notifications
                </span>
              ): currentPageTitle === "User Documents" ? (
                <span className="logo-text">View And Manage All Documents</span>
              ): currentPageTitle === "Cha Requests" ? (
                <span className="logo-text">Details Of Cha Requests</span>
                ): currentPageTitle === "Cha Services Requests" ? (
                <span className="logo-text">Services View And Manage</span>
                ): currentPageTitle === "Audit Details" ? (
                <span className="logo-text">Fill All The Required Data</span>
                ) : currentPageTitle === "Purchase Details" ? (
                <span className="logo-text">Add Purchase Invoice Details</span>
              ) : currentPageTitle === "Tax Invoice" ? (
                <span className="logo-text">Add Tax Invoice Details</span>
   
                ) : currentPageTitle === "Create Performa Invoice" ? (
                <span className="logo-text">View And Manage Performa Invoice</span>
 
               
                ) : currentPageTitle === "View Performa Invoice" ? (
                <span className="logo-text">View And Download Performa Invoice</span>
) : currentPageTitle === "Invoicing" ? (
                 <span className="logo-text">View and Manage Invoicing</span>
              ) : currentPageTitle === "Intelligence" ? (
                 <span className="logo-text">View Analytics and Insights</span>
              ) : null}
            </div>
          </div>
          <div className="d-flex align-items-center">
            {/* Unified Notification Icon for Admins */}
            {(user?.user_role === "Admin" || user?.user_role === "SuperAdmin") && totalNotificationCount > 0 && (
              <div
                className="notification-icon position-relative me-3"
                ref={unifiedNotificationRef}
              >
                <Badge badgeContent={totalNotificationCount} color="error">
                  <NotificationsIcon
                    onMouseEnter={() => {
                      if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                      }
                      setUnifiedNotificationDropdown(true);
                    }}
                    onMouseLeave={() => {
                      timeoutRef.current = setTimeout(() => {
                        setUnifiedNotificationDropdown(false);
                      }, 200);
                    }}
                    className="blink-animation notification-icon"
                  />
                </Badge>
                {unifiedNotificationDropdown && (
                  <div
                    className="notification-tooltip-admin position-absolute z-3"
                    onMouseEnter={() => {
                      if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                      }
                    }}
                    onMouseLeave={() => {
                      timeoutRef.current = setTimeout(() => {
                        setUnifiedNotificationDropdown(false);
                      }, 200);
                    }}
                    onMouseDown={() => {
                      if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                      }
                    }}
                  >
                    {/* Arrow pointing up */}
                    <div className="notification-arrow"></div>
 
                    <div className="notification-header">
                      <NotificationsIcon className="notification-header-icon" />
                      <h6 className="notification-header-title">
                        Notifications
                      </h6>
                    </div>
 
                    {/* Verification Section */}
                    {(unverifiedUsers > 0 || gstUpdateRequests > 0 || warehouseRequests > 0) && (
                      <div className="verification-section">
                        <h6 className="verification-title">
                          🔍 Verification Requests
                        </h6>
                        {unverifiedUsers > 0 && (
                          <p
                            className="unverified-users-item"
                            onClick={() => navigate("/unverified-users")}
                          >
                            There are{" "}
                            <strong style={{ color: "#dc3545" }}>
                              {unverifiedUsers}
                            </strong>{" "}
                            account verification requests awaiting your attention.
                          </p>
                        )}
                        {gstUpdateRequests > 0 && (
                          <p
                            className="gst-requests-item"
                            onClick={() => navigate("/gst-update-requests")}
                          >
                            There are{" "}
                            <strong style={{ color: "#0c5460" }}>
                              {gstUpdateRequests}
                            </strong>{" "}
                            GST update requests awaiting your attention.
                          </p>
                        )}
                        {warehouseRequests > 0 && (
                          <p
                            className="warehouse-requests-item"
                            onClick={() => navigate("/warehouse-add-requests")}
                            style={{
                              backgroundColor: "#e8f5e8",
                              padding: "8px 12px",
                              borderRadius: "4px",
                              margin: "4px 0",
                              cursor: "pointer",
                              border: "1px solid #28a745"
                            }}
                          >
                            There are{" "}
                            <strong style={{ color: "#28a745" }}>
                              {warehouseRequests}
                            </strong>{" "}
                            warehouse requests awaiting your attention.
                          </p>
                        )}
                      </div>
                    )}
 
                    {/* Activity Section */}
                    <div className="activity-section">
                      <h6>
                        📋 Activity Notifications
                      </h6>
                      {notifications.length > 0 ? (
                        <>
                          {notifications.slice(0, 3).map((notif) => (
                            <p
                              key={notif.id}
                              className="activity-notification-item"
                              onClick={() => {
                                // Handle notification click, e.g., navigate to relevant page
                                if (notif.type === "quotation_accepted") {
                                  navigate(`/view-quotation/${notif.enq_id}`);
                                } else if (notif.type === "callback_requested") {
                                  navigate(`/view-enquiry-details/${notif.enq_id}`);
                                } else if (notif.type === "new_enquiry") {
                                  navigate(`/send-quotation/${notif.enq_id}`);
                                } else if (notif.type === "enquiry_updated") {
                                  navigate(`/my-enquiry/view-enquiry-details/${notif.enq_id}`);
                                } else if (notif.type === "quotation_sent") {
                                  navigate(`/view-quotation/${notif.enq_id}`);
                                } else if (notif.type === "si_created" || notif.type === "si_updated") {
                                  navigate(`/view-si/admin/${notif.enq_id}`);
                                } else if (notif.type === "documents_uploaded") {
                                  navigate(`/view-booking-documents/${notif.enq_id}`);
                                }
                                // Mark as read
                                markNotificationAsRead(notif.id);
                              }}
                            >
                              {notif.message}
                            </p>
                          ))}
                          {notifications.length > 3 && (
                            <button
                              className="view-all-button"
                              onClick={() => navigate("/all-notifications")}
                            >
                              View All Notifications
                            </button>
                          )}
                        </>
                      ) : (
                        <p className="no-activity-text">
                          No activity notifications.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
 
            {/* Regular Notification Icon for Non-Admins */}
            {(user?.user_role !== "Admin" && user?.user_role !== "SuperAdmin") && notificationCount > 0 && (
              <div
                className="notification-icon position-relative me-3"
                ref={unifiedNotificationRef}
              >
                <Badge badgeContent={notificationCount} color="error">
                  <NotificationsIcon
                    onMouseEnter={() => {
                      if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                      }
                      setUnifiedNotificationDropdown(true);
                    }}
                    onMouseLeave={() => {
                      timeoutRef.current = setTimeout(() => {
                        setUnifiedNotificationDropdown(false);
                      }, 200);
                    }}
                    className="blink-animation notification-icon"
                  />
                </Badge>
                {unifiedNotificationDropdown && (
                  <div
                    className="notification-tooltip-user position-absolute z-3"
                    onMouseEnter={() => {
                      if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                      }
                    }}
                    onMouseLeave={() => {
                      timeoutRef.current = setTimeout(() => {
                        setUnifiedNotificationDropdown(false);
                      }, 200);
                    }}
                    onMouseDown={() => {
                      if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                      }
                    }}
                  >
                    {/* Arrow pointing up */}
                    <div className="notification-arrow"></div>
                    <div className="notification-header">
                      <NotificationsIcon className="notification-header-icon" />
                      <h6 className="notification-header-title">
                        Notifications
                      </h6>
                    </div>
                    {notifications.length > 0 ? (
                      <>
                        {notifications.slice(0, 3).map((notif) => (
                          <p
                            key={notif.id}
                            className="user-notification-item"
                            onClick={() => {
                              // Handle notification click, e.g., navigate to relevant page
                              if (notif.type === "quotation_accepted") {
                                navigate(`/view-quotation/${notif.enq_id}`);
                              } else if (notif.type === "callback_requested") {
                                navigate(`/view-enquiry-details/${notif.enq_id}`);
                              } else if (notif.type === "new_enquiry") {
                                navigate(`/send-quotation/${notif.enq_id}`);
                              } else if (notif.type === "enquiry_updated") {
                                navigate(`/my-enquiry/view-enquiry-details/${notif.enq_id}`);
                              } else if (notif.type === "quotation_sent") {
                                navigate(`/view-quotation/${notif.enq_id}`);
                              } else if (notif.type === "si_created" || notif.type === "si_updated") {
                                navigate(`/view-si/admin/${notif.enq_id}`);
                              } else if (notif.type === "documents_uploaded") {
                                navigate(`/view-booking-documents/${notif.enq_id}`);
                              }
                              // Mark as read
                              markNotificationAsRead(notif.id);
                            }}
                          >
                            {notif.message}
                          </p>
                        ))}
                        {notifications.length > 3 && (
                          <button
                            className="view-more-button"
                            onClick={() => navigate("/all-notifications")}
                          >
                            View More
                          </button>
                        )}
                      </>
                    ) : (
                      <p className="no-notifications-text">
                        No new notifications.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="user-menu position-relative" ref={dropdownRef}>
              <div
                onClick={toggleDropdown}
                className="user-name mb-0 d-flex align-items-center user-name-cursor"
              >
                {user?.full_name || "User"}
                <div className="avatar ms-2">
                  <img
                    src={user?.avatar || userimg}
                    alt="User Avatar"
                    className="img-avatar"
                  />
                  <span className="avatar-status badge-success"></span>
                </div>
              </div>
 
              {dropdown && (
                <div className="user-box position-absolute">
                  <div className="user-hdr">
                    {user?.avatar ? (
                      <img src={user.avatar} alt="User Avatar" />
                    ) : (
                      <AccountCircleIcon fontSize="large" />
                    )}
                    <div className="user-name">
                      <h6>{user?.full_name}</h6>
                      <p>{user?.email}</p>
                    </div>
                  </div>
                  <div className="routeopt">
                    <div className="out" onClick={handleLogout}>
                      <i className="bi bi-box-arrow-right"></i> Sign Out
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
 
export default Header;
 
 
 